import {
    FETCH_WORKOUTS_FETCHING,
    FETCH_WORKOUTS_FINISHED,
    FETCH_PROVIDER_WORKOUTS_FETCHING,
    FETCH_PROVIDER_WORKOUTS_FINISHED,
    UPDATE_WORKOUTS_FILTER,
    RESET_WORKOUTS_FILTER,
    FETCH_WHEELCHAIR_WORKOUTS_FETCHING,
    FETCH_WHEELCHAIR_WORKOUTS_FINISHED
} from 'actions/ActionTypes.js';

import FuzzySearch from 'fuzzy-search';

const INITIAL_STATE = {
    isFetching: false,
    wheelchairWorkoutsFetching: false,
    entity: [],
    filteredWorkouts: [],
    providerWorkouts: [],
    wheelchairWorkouts: [],
    primaryMuscles: [],
    goals: [],
    sportTypes: [],
    difficultyMapper: {},
    equipment: {}
};

const workoutsFetching = (state) => ({
    ...state,
    isFetching: true
});

const providerWorkoutsFetching = (state) => ({
    ...state,
    isFetching: true
});

const wheelchairWorkoutsFetching = (state) => ({
    ...state,
    wheelchairWorkoutsFetching: true
});

const workoutsFetchFinished = (state, action) => {
    return {
        ...state,
        entity: action.workouts,
        filteredWorkouts: [], // filteredWorkouts should be populated only after provider workouts are fetched
        isFetching: false,
        lastUpdated: action.receivedAt
    };
};

const providerWorkoutsFetchFinished = (state, action) => {
    return {
        ...state,
        providerWorkouts: action.workouts,
        filteredWorkouts: setFilteredWorkouts(state, action),
        primaryMuscles: action.primaryMuscles,
        goals: action.goals,
        sportTypes: action.sportTypes,
        difficultyMapper: action.difficultyMapper,
        equipment: action.equipment,
        isFetching: false,
        lastUpdated: action.receivedAt
    };
};

const wheelchairWorkoutsFetchFinished = (state, action) => {
    return {
        ...state,
        wheelchairWorkouts: action.wheelchairWorkouts,
        filteredWorkouts: setWheelchairFilteredWorkouts(state, action),
        primaryMuscles: action.primaryMuscles,
        goals: action.goals,
        sportTypes: action.sportTypes,
        difficultyMapper: action.difficultyMapper,
        equipment: action.equipment,
        wheelchairWorkoutsFetching: false,
        lastUpdated: action.receivedAt
    };
};

const setWheelchairFilteredWorkouts = (state, action) => {
    // Don't reset the filteredWorkouts if the user has already filtered the list
    if (state.filteredWorkouts.length !== state.wheelchairWorkouts.length) {
        return state.filteredWorkouts;
    } else {
        return action.wheelchairWorkouts;
    }
};

const setFilteredWorkouts = (state, action) => {
    // Don't reset the filteredWorkouts if the user has already filtered the list
    if (state.filteredWorkouts.length !== state.providerWorkouts.length) {
        return state.filteredWorkouts;
    } else {
        return action.workouts;
    }
};

const updateFilteredWorkouts = (state, action) => {
    let isWheelchairModeEnabled = action.isWheelchairModeEnabled;
    let filteredWorkouts = isWheelchairModeEnabled ? state.wheelchairWorkouts : state.providerWorkouts;
    let filters = action.enabledFilters;

    if (filters.SEARCH) {
        let searcher = new FuzzySearch(filteredWorkouts, [
            'workoutName',
            'sportTypeKey',
            'difficulty',
            'primaryMuscles',
            'goals',
            'sportTypes'
        ]);
        filteredWorkouts = searcher.search(filters.SEARCH);
    }

    if (filters.ACTIVITY && filters.ACTIVITY[0] !== 'ALL') {
        filteredWorkouts = filteredWorkouts.filter((workout) => filters.ACTIVITY.includes(workout.sportTypeKey));
    }

    if (filters.DIFFICULTY && filters.DIFFICULTY.length !== 0 && filters.DIFFICULTY[0] !== 'ALL') {
        // ALL is always index 0 if it is selected
        filteredWorkouts = filteredWorkouts.filter((workout) => filters.DIFFICULTY.includes(workout.difficulty));
    }

    if (filters.DURATION && filters.DURATION.length !== 0 && filters.DURATION[0] !== 'ALL') {
        let durations = {};

        for (let durationIdx = 0; durationIdx < filters.DURATION.length; durationIdx++) {
            if (filters.DURATION[durationIdx] === 'LT20') {
                durations[-1] = true;

                if (durations[1199]) {
                    delete durations[1199];
                } else {
                    durations[1199] = true;
                }
            } else if (filters.DURATION[durationIdx] === '20TO40') {
                if (durations[1199]) {
                    delete durations[1199];
                } else {
                    durations[1199] = true;
                }

                if (durations[2401]) {
                    delete durations[2401];
                } else {
                    durations[2401] = true;
                }
            } else if (filters.DURATION[durationIdx] === '40TO60') {
                if (durations[2401]) {
                    delete durations[2401];
                } else {
                    durations[2401] = true;
                }

                if (durations[3601]) {
                    delete durations[3601];
                } else {
                    durations[3601] = true;
                }
            } else if (filters.DURATION[durationIdx] === 'GT60') {
                if (durations[3601]) {
                    delete durations[3601];
                } else {
                    durations[3601] = true;
                }

                durations['Infinity'] = true;
            }
        }

        let durationFilter = Object.keys(durations).sort((a, b) => +a - +b);

        filteredWorkouts = filteredWorkouts.filter((workout) => {
            let result = false;
            for (let durationFilterIdx = 0; durationFilterIdx < durationFilter.length; durationFilterIdx += 2) {
                if (
                    workout.duration > durationFilter[durationFilterIdx] &&
                    workout.duration < durationFilter[durationFilterIdx + 1]
                ) {
                    result = true;
                }
            }

            return result;
        });
    }

    if (filters.PRIMARY_MUSCLES && filters.PRIMARY_MUSCLES.length !== 0 && filters.PRIMARY_MUSCLES[0] !== 'ALL') {
        let filterStr = ',' + filters.PRIMARY_MUSCLES.join(',') + ',';

        filteredWorkouts = filteredWorkouts.filter((workout) => {
            return (
                workout.primaryMuscles &&
                workout.primaryMuscles.some((primaryMuscle) => filterStr.indexOf(',' + primaryMuscle + ',') >= 0)
            );
        });
    }

    if (filters.GOALS && filters.GOALS.length !== 0 && filters.GOALS[0] !== 'ALL') {
        let goalFilter = ',' + filters.GOALS.join(',') + ',';

        filteredWorkouts = filteredWorkouts.filter((workout) => {
            return workout.goals.some((goal) => goalFilter.indexOf(',' + goal + ',') >= 0);
        });
    }

    if (filters.SPORT_TYPES && filters.SPORT_TYPES.length !== 0 && filters.SPORT_TYPES[0] !== 'ALL') {
        let filterStr = ',' + filters.SPORT_TYPES.join(',') + ',';

        filteredWorkouts = filteredWorkouts.filter((workout) => {
            return (
                workout.sportTypes &&
                workout.sportTypes.some((sportType) => filterStr.indexOf(',' + sportType + ',') >= 0)
            );
        });
    }

    return {
        ...state,
        filteredWorkouts: filteredWorkouts
    };
};

const resetFilteredWorkouts = (state, action) => {
    let isWheelchairModeEnabled = action.isWheelchairModeEnabled;
    let filteredWorkouts = isWheelchairModeEnabled ? state.wheelchairWorkouts : state.providerWorkouts;
    return {
        ...state,
        filteredWorkouts: filteredWorkouts
    };
};

const handlers = {
    [FETCH_WORKOUTS_FETCHING]: workoutsFetching,
    [FETCH_WORKOUTS_FINISHED]: workoutsFetchFinished,
    [FETCH_PROVIDER_WORKOUTS_FETCHING]: providerWorkoutsFetching,
    [FETCH_PROVIDER_WORKOUTS_FINISHED]: providerWorkoutsFetchFinished,
    [UPDATE_WORKOUTS_FILTER]: updateFilteredWorkouts,
    [RESET_WORKOUTS_FILTER]: resetFilteredWorkouts,
    [FETCH_WHEELCHAIR_WORKOUTS_FETCHING]: wheelchairWorkoutsFetching,
    [FETCH_WHEELCHAIR_WORKOUTS_FINISHED]: wheelchairWorkoutsFetchFinished
};

export const getWorkouts = (state) => {
    return state.workouts;
};

export const getProviderWorkouts = (state) => {
    return state.workouts.providerWorkouts;
};

export const getPrimaryMuscles = (state) => {
    return state.workouts.primaryMuscles;
};

export const getEquipmentMapper = (state) => {
    return state.workouts.equipment;
};

export const getDifficultyMapper = (state) => {
    return state.workouts.difficultyMapper;
};

export const getFilteredWorkouts = (state) => {
    return state.workouts.filteredWorkouts;
};

export const getWorkoutGoals = (state) => {
    return state.workouts.goals;
};

export const getWorkoutSportTypes = (state) => {
    return state.workouts.sportTypes;
};

export const getWheelchairWorkouts = (state) => {
    return state.workouts.wheelchairWorkouts;
};

export const isWheelchairWorkoutsFetching = (state) => {
    return state.workouts.wheelchairWorkoutsFetching;
};

export default function workouts(state = INITIAL_STATE, action = {}) {
    const handler = handlers[action.type];

    if (!handler) return state;
    return handler(state, action);
}
